import { Location } from "@Clients/LocationsClient";

// Sentinel for "no parent" (root). FormAutocomplete deals in strings, so we use
// an empty string and translate to null at the call site.
export const ROOT_PARENT_VALUE = "";

export type ParentOption = { value: string; label: string };

/** Full path label for a location, e.g. "Warehouse / Aisle 3 / Shelf B". */
export function buildLocationLabel(location: Location): string {
  const ancestors = (location.path ?? []).map((p) => p.name);
  return [...ancestors, location.name].join(" / ");
}

/**
 * True when `candidate` is `targetId` itself or lives anywhere beneath it — i.e.
 * choosing it as `targetId`'s parent would create a cycle. Uses the precomputed
 * `path` (root→parent) each location carries from `get_locations_with_paths`.
 */
export function isSelfOrDescendant(candidate: Location, targetId: string): boolean {
  if (candidate.id === targetId) return true;
  return (candidate.path ?? []).some((ancestor) => ancestor.id === targetId);
}

/**
 * Parent-location options for the location form, led by a "No parent (root)"
 * choice. When `excludeId` is given (editing a location), its own subtree is
 * omitted so a folder can never be moved inside itself or a descendant.
 */
export function buildParentOptions(
  locations: Location[],
  excludeId?: string
): ParentOption[] {
  const eligible = locations
    .filter((l) => !excludeId || !isSelfOrDescendant(l, excludeId))
    .map((l) => ({ value: l.id, label: buildLocationLabel(l) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [{ value: ROOT_PARENT_VALUE, label: "No parent (root)" }, ...eligible];
}
